import { CIMSModal } from 'components/CIMSModal'
import { usePayItemsModuleContext } from '../../PayItemsModuleContext'
import { Form, List, Typography, Button, Space } from 'antd'
import {
  PayItemConnectionOrderInput,
  InternalPayItemConnectionOrderInput,
  PayItemConnectionGroupOrderInput,
  useEditPayItemSummaryOrderMutation,
} from 'schema'
import ArrowButtons from './components/ArrowButtons'
import { useState } from 'react'
import { simpleEditNotification } from 'hooks/notificationHandler'
import { DefaultMutationHookOptions } from 'utils/defaults'
import { useGlobalState } from 'context/GlobalStateContext'
import { internalOrConnection } from '../../PayItemsModuleContext/utils'
import { EditOrderFormListValue } from './types'
import { getLabel } from './utils'
import { useCheckPermission } from 'hooks/useCheckPermission'
import { Permission } from 'shared'
import CIMSCollapse from 'components/CIMSCollapse'
import GroupedListItem from '../GroupedListItem'
import UngroupedListItem from '../UngroupedListItem'
import { errorHandler } from 'hooks/errorHandler'

export const EditOrderModal: React.FC = () => {
  const {
    state: { projectGlobalId: projectId },
  } = useGlobalState()
  const { refetch, sorted } = usePayItemsModuleContext()
  const [editing, setEditing] = useState(false)
  const [edit] = useEditPayItemSummaryOrderMutation({
    ...DefaultMutationHookOptions,
    onCompleted: () => simpleEditNotification('Pay Item Summary order'),
  })
  const initialValues: EditOrderFormListValue = {
    orders: (sorted ?? []).map((item) => {
      if (item.__typename === 'PayItemConnectionGroup') {
        const { id, __typename, children } = item
        return { __typename, id, children: children.map(internalOrConnection) }
      }
      return internalOrConnection(item)
    }),
  }
  const [visible, setVisible] = useState(false)
  const can = useCheckPermission(Permission.EditPayItemSummary)
  if (!can) return null
  return (
    <Space direction="vertical">
      <Button type={'primary'} onClick={() => setVisible(true)}>
        Edit Order
      </Button>
      <CIMSModal
        title="Edit Summary Order"
        visible={visible}
        onCancel={() => setVisible(false)}
        onOk={async ({ orders }) => {
          setEditing(true)
          if (!projectId) return errorHandler('projectId is not defined')
          await edit({
            variables: {
              input: {
                projectId,
                connections: orders.reduce((inputs, item, i) => {
                  if (item.__typename === 'PayItemConnection')
                    inputs.push({ id: item.id, order: i, groupId: null })
                  else if (item.__typename === 'PayItemConnectionGroup')
                    item.children.forEach((child, j) => {
                      if (child.__typename === 'PayItemConnection')
                        inputs.push({
                          id: child.id,
                          order: j,
                          groupId: item.id,
                        })
                    })
                  return inputs
                }, Array<PayItemConnectionOrderInput>()),
                internalConnections: orders.reduce((inputs, item, i) => {
                  if (item.__typename === 'ProjectToInternalConnection')
                    inputs.push({
                      internalConnectionId: item.id,
                      order: i,
                      groupId: null,
                    })
                  else if (item.__typename === 'PayItemConnectionGroup')
                    item.children.forEach((child, j) => {
                      if (child.__typename === 'ProjectToInternalConnection')
                        inputs.push({
                          internalConnectionId: child.id,
                          order: j,
                          groupId: item.id,
                        })
                    })
                  return inputs
                }, Array<InternalPayItemConnectionOrderInput>()),
                groups: orders.reduce((inputs, item, order) => {
                  if (item.__typename === 'PayItemConnectionGroup')
                    inputs.push({ id: item.id, order })
                  return inputs
                }, Array<PayItemConnectionGroupOrderInput>()),
              },
            },
          })
          setEditing(false)
          refetch()
          setVisible(false)
        }}
        initialFormValues={initialValues}
        width="narrow"
        loading={editing}
        disabled={editing}
      >
        <Form.List name="orders">
          {(fields, { move }) => (
            <List
              size="small"
              dataSource={fields}
              renderItem={({ key, name }, index) => {
                const item = sorted[key]
                if (!item) return null
                if (item.__typename === 'PayItemConnectionGroup')
                  return (
                    <GroupedListItem key={key}>
                      <CIMSCollapse
                        ghost
                        panels={[
                          {
                            header: {
                              children: (
                                <>
                                  {item.name}{' '}
                                  <Form.Item hidden name={[name, 'id']} />
                                  <Form.Item
                                    hidden
                                    name={[name, '__typename']}
                                  />
                                </>
                              ),
                            },
                            key,
                            extra: {
                              type: 'count',
                              count: item.children.length,
                              extraPosition: 'right',
                              extra: (
                                <ArrowButtons
                                  index={index}
                                  length={fields.length}
                                  move={move}
                                />
                              ),
                            },
                            children: !item.children.length ? null : (
                              <Form.List name={[name, 'children']}>
                                {(children, { move: moveChild }) => {
                                  return (
                                    <List
                                      dataSource={children}
                                      renderItem={(child, childIndex) => {
                                        /**
                                         * this becomes undefined on close, but is
                                         * defined again on open
                                         */
                                        const ref = item.children[child.key]
                                        const label = ref
                                          ? getLabel(ref)
                                          : 'Unknown'
                                        const n = (itemName: string) => [
                                          child.name,
                                          itemName,
                                        ]
                                        return (
                                          <List.Item
                                            key={child.key}
                                            style={{
                                              display: 'flex',
                                              justifyContent: 'space-between',
                                            }}
                                          >
                                            <Typography.Text>
                                              <Form.Item
                                                hidden
                                                name={n('id')}
                                              />
                                              <Form.Item
                                                hidden
                                                name={n('__typename')}
                                              />
                                              {label}
                                            </Typography.Text>
                                            <ArrowButtons
                                              index={childIndex}
                                              length={children.length}
                                              move={moveChild}
                                            />
                                          </List.Item>
                                        )
                                      }}
                                    />
                                  )
                                }}
                              </Form.List>
                            ),
                          },
                        ]}
                      />
                    </GroupedListItem>
                  )
                else if (
                  item.__typename === 'PayItemConnection' ||
                  item.__typename === 'ProjectToInternalConnection'
                ) {
                  const label = getLabel(item)
                  return (
                    <UngroupedListItem key={key} paddingLeft={56}>
                      <Typography.Text>
                        {label}
                        <Form.Item hidden name={[name, 'id']} />
                        <Form.Item hidden name={[name, '__typename']} />
                      </Typography.Text>
                      <ArrowButtons
                        index={index}
                        length={fields.length}
                        move={move}
                      />
                    </UngroupedListItem>
                  )
                }
                return null
              }}
            />
          )}
        </Form.List>
      </CIMSModal>
    </Space>
  )
}

export default EditOrderModal
